import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const tiers = [
  {
    name: "Starter",
    price: "0",
    description: "Perfect for casual bettors testing the waters.",
    features: ["Daily Free Tips", "Basic Stats", "Community Access", "Standard Support"],
    cta: "Start Free",
    popular: false,
    tier: "free",
  },
  {
    name: "Pro",
    price: "29",
    description: "For serious bettors who want consistent profit.",
    features: ["All Starter Features", "Premium Daily Tips (5+)", "In-Depth Analysis", "Bankroll Management", "Priority Support"],
    cta: "Get Pro",
    popular: true,
    tier: "pro",
  },
  {
    name: "VIP Syndicate",
    price: "99",
    description: "Elite level access to insider information.",
    features: ["All Pro Features", "High Roller Tips", "Direct Expert Access", "Live In-Play Alerts", "Whale Moves Tracking"],
    cta: "Join Syndicate",
    popular: false,
    tier: "vip",
  },
];

export function Pricing() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  const handleSubscribe = (tier: string) => {
    if (tier === "free") {
      toast({
        title: "Welcome to Betting Brain!",
        description: "You're all set with our free tier. Check out today's tips above!",
      });
      return;
    }

    if (tier === "pro") {
      if (!isAuthenticated) {
        setLocation('/register?redirect=subscribe');
        return;
      }
      setLocation('/subscribe');
      return;
    }

    if (tier === "vip") {
      toast({
        title: "VIP Syndicate Coming Soon",
        description: "We're putting the finishing touches on our elite VIP experience. Stay tuned for the launch!",
      });
      return;
    }
  };

  return (
    <section className="py-24 relative" id="pricing">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background pointer-events-none" />
      
      <div className="container px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">CHOOSE YOUR <span className="text-primary">EDGE</span></h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Flexible plans designed for every level of bettor. Cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Card className={`h-full glass border-white/10 flex flex-col ${tier.popular ? 'ring-2 ring-primary relative' : ''}`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground font-bold px-4 py-1 rounded-full text-sm">
                    MOST POPULAR
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <h3 className="text-xl font-bold text-muted-foreground uppercase tracking-widest mb-2">{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-white">$</span>
                    <span className="text-6xl font-display font-bold text-white">{tier.price}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 h-10">{tier.description}</p>
                </CardHeader>
                <CardContent className="flex-1">
                  <ul className="space-y-4 mt-6">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-sm">
                        <div className={`rounded-full p-1 ${tier.popular ? 'bg-primary/20 text-primary' : 'bg-white/10 text-white'}`}>
                          <Check className="w-3 h-3" />
                        </div>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    onClick={() => handleSubscribe(tier.tier)}
                    data-testid={`button-subscribe-${tier.tier}`}
                    className={`w-full font-bold h-12 ${tier.popular ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : 'bg-white/10 hover:bg-white/20 text-white border-transparent'}`}
                    variant={tier.popular ? "default" : "outline"}
                  >
                    {tier.cta}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
